// Story 8.1 (FR38) — the CalibrationGate's data source. Queried ONLY for a
// clinician without the already_certified bypass claim (the gate short-circuits
// on the claim, so post-backfill users pay zero queries). Story 8.7's
// calibration board hydrates from the same key; Story 8.5 invalidates it on
// calibration completion so the gate opens without a re-login.
//
// Epic 20 close (review finding L5-1) — WHILE THE CLINICIAN IS HELD, THIS QUERY
// POLLS. Story 20.2's hold copy promises that a super admin's extension or
// reactivation "restore[s] your case studies immediately", and nothing in the
// app could deliver it: the two FR86 writes happen in the ADMIN's browser and
// invalidate only admin-side keys, so no invalidation can cross to the
// clinician's session. The only `invalidateQueries` on this key anywhere is
// behind a SCORED submission, which a held clinician structurally cannot reach —
// their submit CTA is not rendered — and the held screen offers no navigation
// that would remount past `staleTime`. There is no push channel in this
// frontend. A bounded poll is therefore the only mechanism available, and the
// repo already runs exactly this one: `components/billing/useLapseStatus.ts`
// re-checks every 30s and escalates focus to "always" ONLY while a lapse window
// is open (AC5, story 10.5). Same shape, same reasons, applied to the same kind
// of fact — a server-side release the user is sitting there waiting for.

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { CalibrationStatusData } from "@/hooks/queries/calibration-status-types";

export const CALIBRATION_STATUS_QUERY_KEY = "calibration-status";

/**
 * The user-visible ceiling on how long a lifted hold can keep showing as held.
 * `useLapseStatus`'s `LAPSE_RECHECK_INTERVAL_MS` value, for the same job.
 */
export const CALIBRATION_HOLD_RECHECK_INTERVAL_MS = 30_000;

/**
 * True only on the one state the clinician cannot leave by themselves.
 *
 * `held` and NOT `due_soon`: a clinician still inside their window can act, and
 * whatever changes for them changes through their own writes, which invalidate
 * this key already. Held is the state where the release is somebody else's
 * action in somebody else's browser, so it is the only one worth a network
 * round trip every 30s. `not_applicable` and `active` never poll, which keeps
 * the whole clinician population quiet — the escalation is scoped to the people
 * waiting for it, exactly as the lapse surface scopes its own.
 */
function isDeadlineHeld(data: CalibrationStatusData | undefined): boolean {
  return data?.deadline_state === "held";
}

// The query key encodes clinician_id even though every caller today reads SELF
// (clinicianId undefined). GET /calibration/status already accepts an optional
// ?clinician_id (the Story 8.6 role-scoped admin read); keying on the bare constant
// would let a future admin mount collide two targets in cache — one clinician's board
// shown for another. Threading it now (conventions lesson #16 + seam-86-admin-card-mount)
// means the admin mount only has to PASS clinicianId; the key AND the request already
// carry it. SELF callers pass nothing → ["calibration-status", undefined] + the plain
// URL, so today's behaviour is unchanged.
export function calibrationStatusQueryKey(clinicianId?: string) {
  return [CALIBRATION_STATUS_QUERY_KEY, clinicianId] as const;
}

// The request half of lesson #16 (kept in lockstep with the query key above): SELF
// callers hit the bare endpoint; an admin mount appends the target as an ENCODED
// ?clinician_id (never raw — the id flows into the query string). Extracted as a pure
// helper so the URL contract is unit-testable without spinning up the query.
export function calibrationStatusPath(clinicianId?: string) {
  return clinicianId
    ? `/calibration/status?clinician_id=${encodeURIComponent(clinicianId)}`
    : "/calibration/status";
}

export function useCalibrationStatus(opts: { enabled: boolean; clinicianId?: string }) {
  return useQuery({
    queryKey: calibrationStatusQueryKey(opts.clinicianId),
    queryFn: () => apiFetch<CalibrationStatusData>(calibrationStatusPath(opts.clinicianId)),
    enabled: opts.enabled,
    // Calibration state changes rarely (a case submission / 8.5 completion —
    // both invalidate the key); 5 min matches useCurrentUser's boot-read posture.
    staleTime: 5 * 60 * 1000,
    // gcTime > staleTime (PR #528 review): TanStack v5 defaults gcTime to 5 min —
    // the SAME window as staleTime above, so the cache entry could be evicted the
    // instant it goes stale. A clinician who navigates off a gated route and back
    // just after the stale mark would then pay a fresh network round-trip (a
    // FullPageLoading flash) instead of a cached read. Hold the entry 30 min so a
    // stale-but-present cache still serves the gate decision instantly on remount.
    gcTime: 30 * 60 * 1000,
    // Offline-aware boot read: fire even when the browser thinks it's offline
    // (the gate fails OPEN on error — backend CALIB-001 is the authority) and
    // never retry into a redirect delay.
    networkMode: "always",
    retry: false,
    // L5-1 — the release channel for a held clinician, and the ONLY one that
    // exists (see the file header). Polling bypasses `staleTime` by design; it
    // is armed off the FRESHEST payload, so the very refetch that reports the
    // lift is also the one that disarms the interval.
    refetchInterval: (query) =>
      isDeadlineHeld(query.state.data) ? CALIBRATION_HOLD_RECHECK_INTERVAL_MS : false,
    // Focus escalated to "always" for the held clinician only — the app-wide
    // default is `false` (App.tsx), so an alt-tab back to a held page would
    // otherwise serve the 5-minute-stale cache and keep the hold on screen. The
    // `false` branch preserves that default for everybody else: this is not an
    // unconditional focus refetch on every clinician session.
    refetchOnWindowFocus: (query) => (isDeadlineHeld(query.state.data) ? "always" : false),
  });
}
